import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '../../utils/supabase';
import { getLocalCache, setLocalCache } from '../../utils/localCache';

const COUNTRIES = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'Ireland'];
const CACHE_KEY = 'home_county_counts_v1';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
// (Kept for potential future bucketing; not used in current UI.)

// Rough bounds for UK + Ireland to reject bad geocodes.
// This avoids single outliers pulling county bubbles into mainland Europe.
const UKI_BOUNDS = {
  minLat: 49.5,
  maxLat: 61.5,
  minLon: -11.0,
  maxLon: 2.2,
};

function median(values) {
  const v = (values || []).filter((n) => typeof n === 'number' && !Number.isNaN(n)).sort((a, b) => a - b);
  if (v.length === 0) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

function minValue(values) {
  let m = null;
  for (const n of values || []) {
    if (typeof n !== 'number' || Number.isNaN(n)) continue;
    if (m == null || n < m) m = n;
  }
  return m;
}

function maxValue(values) {
  let m = null;
  for (const n of values || []) {
    if (typeof n !== 'number' || Number.isNaN(n)) continue;
    if (m == null || n > m) m = n;
  }
  return m;
}

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function priceShade(t) {
  // low -> high: slate tint to indigo tint (data-ish, not loud)
  const v = clamp01(t);
  const alpha = 0.06 + v * 0.16;
  return `rgba(79, 70, 229, ${alpha.toFixed(3)})`; // indigo-600
}

function buildCountyStats(counties, courseRows) {
  const countyMetaByName = new Map();
  for (const c of counties || []) {
    countyMetaByName.set(c.county_name, c);
  }

  const counts = new Map();
  const coordLists = new Map(); // county_name -> { lats: number[], lons: number[] }
  const feeLists = new Map(); // county_name -> number[]

  for (const row of courseRows || []) {
    const county = row.course_county;
    if (!county) continue;
    counts.set(county, (counts.get(county) || 0) + 1);

    const lat = row.latitude;
    const lon = row.longitude;
    if (typeof lat === 'number' && typeof lon === 'number') {
      if (
        lat >= UKI_BOUNDS.minLat &&
        lat <= UKI_BOUNDS.maxLat &&
        lon >= UKI_BOUNDS.minLon &&
        lon <= UKI_BOUNDS.maxLon
      ) {
        const prev = coordLists.get(county) || { lats: [], lons: [] };
        prev.lats.push(lat);
        prev.lons.push(lon);
        coordLists.set(county, prev);
      }
    }

    const fee = row.weekend_green_fee;
    if (typeof fee === 'number' && fee > 0 && fee < 1000) {
      const prevFees = feeLists.get(county) || [];
      prevFees.push(fee);
      feeLists.set(county, prevFees);
    }
  }

  const byCountry = {};
  for (const country of COUNTRIES) byCountry[country] = [];

  for (const c of counties || []) {
    const count = counts.get(c.county_name) || 0;
    const list = coordLists.get(c.county_name);
    const latMed = list ? median(list.lats) : null;
    const lonMed = list ? median(list.lons) : null;
    const centroid = latMed != null && lonMed != null ? { lat: latMed, lon: lonMed } : null;

    const fees = feeLists.get(c.county_name) || [];
    const feeMedian = median(fees);
    const feeMin = minValue(fees);
    const feeMax = maxValue(fees);

    byCountry[c.country_of_county] = byCountry[c.country_of_county] || [];
    byCountry[c.country_of_county].push({
      county_name: c.county_name,
      slug_county_name: c.slug_county_name,
      country_of_county: c.country_of_county,
      course_count: count,
      centroid,
      fee_stats: {
        median: feeMedian,
        min: feeMin,
        max: feeMax,
        n: fees.length,
      },
    });
  }

  // Price "heat" relative to other counties in the same country (based on typical/median fee).
  for (const country of Object.keys(byCountry)) {
    const medians = byCountry[country]
      .map((c) => c.fee_stats?.median)
      .filter((n) => typeof n === 'number' && !Number.isNaN(n))
      .sort((a, b) => a - b);

    for (const c of byCountry[country]) {
      const m = c.fee_stats?.median;
      if (typeof m !== 'number' || Number.isNaN(m) || medians.length === 0) {
        c.price_index = null;
        continue;
      }
      // percentile rank (0..1)
      let lo = 0;
      let hi = medians.length - 1;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (medians[mid] < m) lo = mid + 1;
        else hi = mid;
      }
      c.price_index = medians.length === 1 ? 0.5 : lo / (medians.length - 1);
    }
  }

  for (const country of Object.keys(byCountry)) {
    byCountry[country].sort((a, b) => {
      if (b.course_count !== a.course_count) return b.course_count - a.course_count;
      return a.county_name.localeCompare(b.county_name);
    });
  }

  return { byCountry };
}

export default function HomeCountyExplorer({ onData }) {
  const navigate = useNavigate();
  const [activeCountry, setActiveCountry] = useState('England');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ byCountry: {} });

  useEffect(() => {
    const cached = getLocalCache(CACHE_KEY, CACHE_TTL_MS);
    if (cached?.byCountry) {
      setData(cached);
      setIsLoading(false);
      if (onData) onData(cached);
    }

    let cancelled = false;
    const load = async () => {
      setError(null);
      try {
        const { data: counties, error: countiesError } = await supabase
          .from('counties')
          .select('county_name, slug_county_name, country_of_county')
          .in('country_of_county', COUNTRIES)
          .order('country_of_county')
          .order('county_name');
        if (countiesError) throw countiesError;

        // Minimal payload: just what we need to aggregate counts + centroids.
        const { data: courses, error: coursesError } = await supabase
          .from('golf_courses')
          .select('course_county, course_country, latitude, longitude, weekend_green_fee')
          .in('course_country', COUNTRIES)
          .range(0, 12000);
        if (coursesError) throw coursesError;

        const next = buildCountyStats(counties || [], courses || []);
        if (cancelled) return;

        setData(next);
        setLocalCache(CACHE_KEY, next);
        if (onData) onData(next);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load county data');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [onData]);

  const countries = useMemo(() => COUNTRIES, []);
  const activeCounties = data.byCountry?.[activeCountry] || [];

  const handleCountyClick = (slug) => {
    navigate(`/lists/golf-courses/county-ranking/${slug}`);
  };

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/80 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          Explore by County
        </h2>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <section className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/80 overflow-hidden">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
              Explore UK & Ireland golf by county
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-1">
              Ranked courses, maps, green fees, architect insights, and more — data-first.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {countries.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCountry(c)}
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold border transition-colors ${
                  activeCountry === c
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          {isLoading && activeCounties.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 animate-pulse">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i} className="h-10 rounded-lg bg-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {activeCounties.map((county) => (
                <button
                  key={county.slug_county_name}
                  type="button"
                  onClick={() => handleCountyClick(county.slug_county_name)}
                  className="group flex flex-col items-start justify-center gap-1 px-3 py-2 rounded-xl border border-slate-200 hover:border-slate-400 text-left"
                  style={{
                    backgroundColor:
                      typeof county.price_index === 'number'
                        ? priceShade(county.price_index)
                        : undefined,
                  }}
                >
                  <div className="w-full flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-slate-800 group-hover:text-slate-900">
                      {county.county_name}
                    </span>
                    <span className="text-xs font-mono text-slate-600 bg-slate-100 group-hover:bg-white px-2 py-0.5 rounded-full">
                      {county.course_count.toLocaleString()}
                    </span>
                  </div>

                  {county.fee_stats?.n > 0 && (
                    <div className="w-full text-xxs text-slate-600 flex items-center justify-between gap-2">
                      <span className="truncate">
                        Min £{Math.round(county.fee_stats.min).toLocaleString()} · Mid £
                        {Math.round(county.fee_stats.median).toLocaleString()} · Max £
                        {Math.round(county.fee_stats.max).toLocaleString()}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

